import { useCallback } from 'react';
import { exportToPNG, exportToMermaid, exportToJSON } from '../../utils/export';
import { centerCanvasOnNodes } from '../../utils/canvas';
import { ZOOM_MAX, ZOOM_MIN, ZOOM_FACTOR, CANVAS_SIZE } from '../../constants';

export const useCanvasActions = ({
  nodes, connections, postIts, currentMapName,
  scrollContainerRef, zoom, setZoom, zoomRef,
  setMermaidCode, setShowMermaidModal, setCopiedToClipboard
}) => {
  
  const handleZoomStep = useCallback((factor) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const currentZoom = zoomRef.current;
    const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, currentZoom * factor));
    const cx = container.clientWidth / 2;
    const cy = container.clientHeight / 2;
    const svgX = (cx + container.scrollLeft) / currentZoom;
    const svgY = (cy + container.scrollTop) / currentZoom;
    setZoom(newZoom);
    requestAnimationFrame(() => {
      container.scrollLeft = svgX * newZoom - cx;
      container.scrollTop = svgY * newZoom - cy;
    });
  }, [setZoom, scrollContainerRef, zoomRef]);

  const handleZoomIn = useCallback(() => handleZoomStep(ZOOM_FACTOR), [handleZoomStep]);
  const handleZoomOut = useCallback(() => handleZoomStep(1 / ZOOM_FACTOR), [handleZoomStep]);

  const handleResetZoom = useCallback((options = {}) => {
    setZoom(1);

    if (options.centerMap && nodes.length > 0) {
      requestAnimationFrame(() => {
        centerCanvasOnNodes(nodes, scrollContainerRef.current, 1);
      });
    }
  }, [setZoom, nodes, scrollContainerRef]);

  const handleCenterCanvas = useCallback(() => {
    if (nodes.length > 0) {
      centerCanvasOnNodes(nodes, scrollContainerRef.current, zoom);
    }
  }, [nodes, zoom, scrollContainerRef]);

  const handleExportPNG = useCallback(() => {
    exportToPNG(nodes, connections, postIts);
  }, [nodes, connections, postIts]);

  const handleExportJSON = useCallback(() => {
    exportToJSON(nodes, connections, currentMapName, postIts);
  }, [nodes, connections, currentMapName, postIts]);

  const handleExportMermaid = useCallback(() => {
    const code = exportToMermaid(nodes, connections);
    setMermaidCode(code);
    setShowMermaidModal(true);
    setCopiedToClipboard(false);
  }, [nodes, connections, setMermaidCode, setShowMermaidModal, setCopiedToClipboard]);

  return {
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleCenterCanvas,
    handleExportPNG,
    handleExportJSON,
    handleExportMermaid
  };
};
